/**
 * StaleSyncScreen — shown instead of the till when the local cache is too old
 * to bill from offline. See billing/useStaleSyncGate.ts and PLAN Decision 7.
 *
 * Web-only: Desktop has no equivalent because it is used daily and its cache is
 * rarely stale.
 */
import { CloudOff, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { STALE_SYNC_DAYS, type StaleSyncState } from '@/billing/useStaleSyncGate';

interface Props {
  state: StaleSyncState;
  onRetry: () => void;
  isSyncing: boolean;
}

export function StaleSyncScreen({ state, onRetry, isSyncing }: Props) {
  const neverSynced = state.reason === 'never-synced';

  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="w-full max-w-md rounded-lg border border-stroke-hairline/15 bg-bg-surface p-6 shadow-e1">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-warning/10 text-warning-ink">
            <CloudOff size={20} />
          </div>
          <div>
            <div className="text-heading text-fg-primary">
              {neverSynced ? 'Not synced yet' : 'Local data is out of date'}
            </div>
            <div className="text-label uppercase text-fg-tertiary">
              {neverSynced
                ? 'No catalogue on this device'
                : `Last synced ${state.ageDays} day${state.ageDays === 1 ? '' : 's'} ago`}
            </div>
          </div>
        </div>

        <p className="mt-4 text-body text-fg-secondary">
          {neverSynced ? (
            <>
              This device has never synced, so it has no prices or stock to bill from.
              Connect to the internet and sync before billing.
            </>
          ) : (
            <>
              Billing offline is blocked after {STALE_SYNC_DAYS} days without a sync,
              because prices and stock levels are likely to have changed. Reconnect and
              sync to continue.
            </>
          )}
        </p>

        <p className="mt-3 text-body text-fg-tertiary">
          Sales already saved on this device are safe and will sync when you reconnect.
        </p>

        <Button className="mt-6 w-full" onClick={onRetry} disabled={isSyncing}>
          <RefreshCw size={16} className={isSyncing ? 'animate-spin' : undefined} />
          {isSyncing ? 'Syncing…' : 'Retry sync'}
        </Button>
      </div>
    </div>
  );
}
